import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, effect, inject, signal, computed, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { IconsComponent } from '../../../../../../utils/icons/icons.component';
import { MessageContent, WordTimestamps } from '../../../../../../models/agent.models';

import { extractTags } from './extract-tags';

export interface IWordMetadata {
  word: string;
  index: number;
  isHighlighted?: boolean;
  tag?: string;
}

export type AudioState = 'idle' | 'playing' | 'paused' | 'loading' | 'playable';

@Component({
  selector: 'message-content-displayer',
  standalone: true,
  imports: [IconsComponent],
  templateUrl: './message-content-displayer.html',
  styleUrls: ['./message-content-displayer.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageContentDisplayer implements OnDestroy {
  // Inputs
  public message = input.required<MessageContent>();
  // Outputs
  readonly playAudio = output<MessageContent>();
  readonly audioCompleted = output<MessageContent>();
  readonly wordClicked = output<{ word: string; index: number; messageId: string }>();
  // Signal States
  public wordWithMeta = signal<IWordMetadata[]>([]);
  public audioState = signal<AudioState>('idle');
  public alreadyPlayed = false;

  // Signals State computed from the input message
  public wordsWithMetaState: IWordMetadata[] = [];

  public iconState = computed(() => {
    if (this.message()?.isLoading) {
      return 'loading';
    }
    const state = this.audioState();
    if (state === 'playing' || state === 'paused') {
      return state;
    }
    if (this.message()?.audioUrl) {
      return 'playable';
    }
    return 'idle';
  });

  public shouldPlayAudio = computed(() => !!this.message() && this.message()?.shouldPlayAudio);

  messageText = computed(() => {
    const msg = this.message();
    return msg?.text || msg?.content || 'N/A';
  });

  classTag = computed(() => this.message()?.tag);

  hasTranscription = computed(() => {
    return !!this.message()?.transcriptionTimestamps && this.message()?.transcriptionTimestamps.length > 0;
  });

  private audioElement: HTMLAudioElement | null = null;
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const currentMsg = this.message();
      if (currentMsg && this.audioState() !== 'playing' && !this.alreadyPlayed) {
        this.initializeBasedOnMessage(currentMsg);
        if (currentMsg.shouldPlayAudio) {
          this.startAudioPlayback();
        }
      }
    });

    effect(() => {
      this.wordsWithMetaState = this.wordWithMeta();
    });
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupAudio();
  }

  private initializeBasedOnMessage(msg: MessageContent): void {
    if (msg.audioUrl && (!this.audioElement || this.audioElement.src !== msg.audioUrl)) {
      this.initializeAudio(msg.audioUrl);
    } else if (!msg.audioUrl && this.audioElement) {
      this.cleanupAudio();
    }

    if (this.hasTranscription()) {
      const wordsAndTimestamps: (IWordMetadata & WordTimestamps)[] = [];
      msg?.transcriptionTimestamps?.forEach((word, index) => {
        wordsAndTimestamps.push({ ...word, ...this.wordsWithMetaState[index], index });
      });
      this.subcribeToAudioSync(wordsAndTimestamps);
    } else {
      this.subscribeToEndAudio();
      this.initializePlainTextWords(msg.text || msg.content || '');
    }
  }

  private initializeAudio(audioUrl: string): void {
    this.cleanupAudio();
    this.audioElement = new Audio(audioUrl);
    this.audioState.set('playable');
  }

  private subcribeToAudioSync(timestamps: (IWordMetadata & WordTimestamps)[]): void {
    if (!this.audioElement) return;
    this.destroy$.next();

    fromEvent(this.audioElement, 'timeupdate')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.destroy$),
        map(() => this.audioElement?.currentTime || 0),
      )
      .subscribe((currentTime) => {
        const updatedWords = timestamps.map((word, index) => ({
          word: word.word,
          index,
          isHighlighted: currentTime >= word.start - 0.15 && currentTime < word.end + 0.15,
          tag: word.tag,
        }));
        this.wordWithMeta.set(updatedWords);
      });

    fromEvent(this.audioElement, 'ended')
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.destroy$))
      .subscribe(() => {
        const resetWords = this.wordWithMeta().map((word) => ({ ...word, isHighlighted: false }));
        this.wordWithMeta.set(resetWords);
        this.audioState.set('playable');
        const currentMsg = this.message();
        if (currentMsg) {
          this.cleanupAudio();
          currentMsg.shouldPlayAudio = false;
          this.audioCompleted.emit(currentMsg);
        }
      });
  }

  private subscribeToEndAudio(): void {
    if (!this.audioElement) return;

    fromEvent(this.audioElement, 'ended')
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.destroy$))
      .subscribe(() => {
        this.audioState.set('playable');
        const currentMsg = this.message();
        if (currentMsg) {
          this.cleanupAudio();
          currentMsg.shouldPlayAudio = false;
          this.audioCompleted.emit(currentMsg);
        }
      });
  }

  private cleanupAudio(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.removeAttribute('src');
      this.audioElement = null;
      this.destroy$.next();
      const resetWords = this.wordWithMeta().map((word) => ({ ...word, isHighlighted: false }));
      this.wordWithMeta.set(resetWords);
    }
    this.audioState.set('idle');
  }

  onPlayMessage(): void {
    const currentMsg = this.message();
    if (this.audioElement) {
      if (this.audioElement.paused) {
        this.startAudioPlayback();
      } else {
        this.audioElement.pause();
        this.audioState.set('paused');
      }
    } else if (currentMsg?.audioUrl) {
      this.initializeAudio(currentMsg.audioUrl);
      this.startAudioPlayback();
    } else if (currentMsg) {
      this.playAudio.emit(currentMsg);
    }
  }

  private initializePlainTextWords(text: string): void {
    const words = extractTags(text);
    const plainWords = words.map((word, index) => ({
      word: word.word,
      index: index,
      isHighlighted: false,
      tag: word.tag,
    }));
    this.wordWithMeta.set(plainWords);
  }

  onWordClick(wordData: { word: string; index: number } | IWordMetadata): void {
    const currentMsg = this.message();
    if (!currentMsg) return;
    const clickedWord =
      'isHighlighted' in wordData
        ? { word: wordData.word, index: wordData.index, messageId: currentMsg.messageId }
        : { ...wordData, messageId: currentMsg.messageId };
    this.wordClicked.emit(clickedWord);
  }

  private startAudioPlayback(): void {
    if (!this.audioElement) return;
    this.audioElement.play().catch((error) => console.error('Error playing audio:', error));
    this.audioState.set('playing');
    this.alreadyPlayed = true;
  }
}
